import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, Input, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { of, take } from 'rxjs';
import { PrimaryButtonComponent } from '../../basic/buttons/primary-button/primary-button.component';
import { QuestionButtonComponent } from '../../basic/buttons/question-button/question-button.component';
import { BarChartComponent } from '../../basic/chart/bar-chart/bar-chart.component';
import { MarkdownItComponent } from '../../basic/markdown-it/markdown-it.component';
import { MarkdownItPipe } from '../../basic/pipes/markdown-it.pipe';
import { ToLocaleFormat } from '../../basic/pipes/to-locale-format.pipe';
import { TruncatePipe } from '../../basic/pipes/truncate.pipe';
import { Board } from '../../core/board/board';
import { BoardService } from '../../core/board/board.service';
import { MethodologyService } from '../../core/methodology/methodology.service';
import { Objection, ObjectionType } from '../../core/objection/objection';
import { ObjectionService } from '../../core/objection/objection.service';
import { OpenaiModel } from '../../core/openai/openai';
import { getEvaluateGoalPrompt } from '../../core/prompt/evaluation/evaluate-goal.prompt';
import {
  getEvaluateMethodologyFunctions,
  getEvaluateMethodologyPrompt,
} from '../../core/prompt/evaluation/evaluate-methodology.prompt';
import { evaluateObjectionHandlingPrompt } from '../../core/prompt/evaluation/evaluate-objection-handling.prompt';
import { getSessionSummaryPrompt } from '../../core/prompt/evaluation/session-summary.prompt';
import { SessionMessage, SessionMessageUtils } from '../../core/session-message/session-message';
import { SessionTemplate } from '../../core/session-template/session-template';
import { Session, SessionMethodologyEvaluation } from '../../core/session/session';
import { SessionService } from '../../core/session/session.service';
import { StripeProductType } from '../../core/stripe/product.stripe';
import { UserClerkService } from '../../core/user/user-clerk.service';
import { WorkspaceHolderService } from '../../core/workspace/workspace.holder.service';
import { UpgradeToPremiumComponent } from '../../domain/membership/upgrade-to-premium/upgrade-to-premium.component';
import { ObjectionHandlingTableComponent } from '../../domain/objection/objection-handling-table/objection-handling-table.component';
import { PortraitSelectComponent } from '../../domain/portrait/portrait-select/portrait-select.component';
import { ResumePortraitNameComponent } from '../../domain/resume/resume-portrait-name/resume-portrait-name.component';
import { ScoreCardComponent } from '../../domain/score-card/score-card.component';
import { AppColor } from '../../shared/color/color';
import { NumberUtils } from '../../shared/utils/number-utils';
import { BasicPageComponent } from '../_shared/basic-page/basic-page.component';
import { OpenaiService } from './../../core/openai/openai.service';
import { SessionMessageService } from './../../core/session-message/session-message.service';
import { SessionTemplateService } from './../../core/session-template/session.template.service';
import { CustomerTimeRangeComponent } from './customer-time-range/customer-time-range.component';
import { SellerTimeRangeComponent } from './seller-time-range/seller-time-range.component';

@Component({
  selector: 'app-session-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzStepsModule,
    NzButtonModule,
    NzIconModule,
    NzTabsModule,
    NzAvatarModule,
    NzToolTipModule,
    NzPopoverModule,
    NzSkeletonModule,
    NzCollapseModule,

    MatTooltipModule,

    MarkdownItPipe,
    TruncatePipe,

    ScoreCardComponent,

    UpgradeToPremiumComponent,

    QuestionButtonComponent,
    BasicPageComponent,
    PrimaryButtonComponent,
    BarChartComponent,
    ResumePortraitNameComponent,
    SellerTimeRangeComponent,
    CustomerTimeRangeComponent,
    PortraitSelectComponent,
    MarkdownItComponent,
    ObjectionHandlingTableComponent,

    ToLocaleFormat,
  ],
  templateUrl: './session-report.component.html',
  styleUrl: './session-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailsExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('1000ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class SessionReportComponent {
  @ViewChild('videoPlayer') videoElement?: ElementRef<HTMLVideoElement>;

  // session id
  @Input({ required: true }) id?: string;

  isLoadingMethodology = signal<boolean>(false);
  isLoadingGoal = signal<boolean>(false);
  isLoadingSummary = signal<boolean>(false);

  session = signal<Session | undefined>(undefined);

  sessionTemplate = signal<SessionTemplate | undefined>(undefined);

  sessionMessageList = signal<SessionMessage[]>([]);

  emotionalObjectionList = computed<Objection[]>(() => {
    const itemList = this.objectionList();

    const result: Objection[] = [];

    this.session()?.subData?.template?.unsafeSubData?.emotionalObjectionIdList?.forEach((id) => {
      const item = itemList.find((item) => item.id === id);
      if (item) {
        result.push(item);
      }
    });

    return result;
  });

  factObjectionList = computed<Objection[]>(() => {
    const itemList = this.objectionList();

    const result: Objection[] = [];

    this.session()?.subData?.template?.unsafeSubData?.factObjectionIdList?.forEach((id) => {
      const item = itemList.find((item) => item.id === id);
      if (item) {
        result.push(item);
      }
    });

    return result;
  });

  customerTimeRangeList = computed<
    { startTime: DateTime; endTime: DateTime; message: string; sessionMessage: SessionMessage }[]
  >(() => {
    const result = this.sessionMessageList().map((message) => {
      return {
        startTime: DateTime.fromISO(message.subData.customerStartTimeStr),
        endTime: DateTime.fromISO(message.subData.customerEndTimeStr),
        message: message.subData.customerMessage,
        sessionMessage: message,
      };
    });
    return result;
  });

  salesTimeRangeList = computed<
    {
      startTime: DateTime;
      endTime: DateTime;
      message: string;
      sessionMessage: SessionMessage;
    }[]
  >(() => {
    const result = this.sessionMessageList().map((message) => {
      return {
        startTime: DateTime.fromISO(message.subData.salesStartTimeStr),
        endTime: DateTime.fromISO(message.subData.salesEndTimeStr),
        message: message.subData.salesMessage,
        sessionMessage: message,
      };
    });
    return result;
  });

  fullStartTime = computed<DateTime | undefined>(() => {
    return this.session()?.startTime;
  });

  fullEndTime = computed<DateTime | undefined>(() => {
    return this.session()?.endTime;
  });

  calculateLeftPosition = (startTime: DateTime) =>
    computed(() => {
      if (this.fullEndTime() && this.fullStartTime()) {
        const totalDuration = this.fullEndTime()!.diff(this.fullStartTime()!).as('milliseconds');
        const position = startTime.diff(this.fullStartTime()!).as('milliseconds');
        return `${(position / totalDuration) * 100}%`;
      }

      return `0%`;
    });

  calculateWidth = (startTime: DateTime, endTime: DateTime) =>
    computed(() => {
      if (this.fullEndTime() && this.fullStartTime()) {
        const totalDuration = this.fullEndTime()!.diff(this.fullStartTime()!).as('milliseconds');
        const duration = endTime.diff(startTime).as('milliseconds');
        return `${(duration / totalDuration) * 100}%`;
      } else {
        return `0%`;
      }
    });

  summary = signal<string | undefined>(undefined);
  goalEvaluation = signal<string | undefined>(undefined);
  methodologyEvaluation = signal<SessionMethodologyEvaluation | undefined>(undefined);
  boardList = toSignal<Board[]>(this.boardService.getAllBoardList$());

  recordingFileUrl = computed(() => {
    return this.session()?.subData.recordingUrl;
  });

  private retryCount = 0;
  private maxRetries = 2;

  handleVideoError() {
    if (this.retryCount < this.maxRetries) {
      setTimeout(() => {
        this.videoElement?.nativeElement?.load();
        this.retryCount++;
      }, 2000);
    } else {
      console.error('Max retries reached. Unable to load video.');
    }
  }

  score = signal<number | undefined>(undefined);

  chartDataList = computed(() => {
    const methodologyEvaluation = this.methodologyEvaluation();

    if (methodologyEvaluation) {
      return methodologyEvaluation?.evaluations?.map((c: any, i) => {
        let color = AppColor.red;
        if (c.rating > 3) {
          color = AppColor.green;
        } else if (c.rating === 3) {
          color = AppColor.yellow;
        }

        return { id: c.criterion, name: c.criterion, value: c.rating, color };
      });
    }

    return [];
  });

  constructor(
    private router: Router,
    private userClerkService: UserClerkService,
    private openAiService: OpenaiService,
    private sessionService: SessionService,
    private boardService: BoardService,
    private workspaceHolderService: WorkspaceHolderService,
    private sessionMessageService: SessionMessageService,
    private sessionTemplateService: SessionTemplateService,
    private methodologyService: MethodologyService,
    private objectionService: ObjectionService
  ) {}

  objectionList = toSignal(this.objectionService.itemList$(), { initialValue: [] });

  getObjection = (id?: string) => computed(() => this.objectionList().find((objection) => objection.id === id));

  ngOnInit() {
    if (!this.id) {
      this.router.navigate(['/simulated-meetings']);
      return;
    }

    this.sessionService
      .getSessionById$(this.id)
      .pipe(take(1))
      .subscribe((session) => {
        this.session.set(session);

        if (session.parentId) {
          this.sessionTemplateService
            .getById$(session.parentId)
            .pipe(take(1))
            .subscribe((sessionTemplate) => {
              this.sessionTemplate.set(sessionTemplate);

              this.sessionMessageService
                .getSessionMessageListById$(session?.id)
                .pipe(take(1))
                .subscribe(async (messageList) => {
                  messageList.sort((a, b) =>
                    DateTime.fromISO(a.subData.customerStartTimeStr) < DateTime.fromISO(b.subData.customerStartTimeStr)
                      ? -1
                      : 1
                  );

                  for (const message of messageList) {
                    if (message.subData.objectionId && !message.subData.objectionScore) {
                      const objection = this.objectionList().find((c) => c.id === message.subData.objectionId);

                      if (objection && objection.type === ObjectionType.fact) {
                        this.sessionMessageService
                          .evaluateFactBasedObjectionAnswer$(message, messageList, objection)
                          .pipe(take(1))
                          .subscribe();
                      }
                    }
                  }

                  this.sessionMessageList.set(messageList);

                  if (!session.subData.summary) {
                    this.evaluateSessionSummary();
                  } else {
                    this.summary.set(session.subData.summary);
                  }

                  if (!session.subData.goalEvaluation && session.subData.goal?.description) {
                    this.evaluateGoal();
                  } else {
                    this.goalEvaluation.set(session.subData.goalEvaluation);
                  }

                  if (!session.subData.methodologyEvaluation && session.subData.methodology) {
                    this.evaluateMethodology(false);
                  } else {
                    this.selectedMethodologyCriteria.set({
                      criterion: 'Overall',
                      rating: session.subData.methodologyEvaluation?.overallScore,
                      reason: session.subData.methodologyEvaluation?.overallReason,
                      improvements: session.subData.methodologyEvaluation?.overallImprovements,
                    });
                    this.methodologyEvaluation.set(session.subData.methodologyEvaluation);
                    this.score.set(session.subData.score);
                  }

                  if (session.subData.objectionHandlingEvaluation) {
                    this.objectionHandlingEvaluation.set(session.subData.objectionHandlingEvaluation);
                  }
                });
            });
        }
      });
  }

  isPremium = computed(() => {
    return this.workspaceHolderService.paidStatus()?.type === StripeProductType.premium;
  });

  isMethodologyDetailsVisible = false;
  onMethodologyDetailsClick() {
    this.isMethodologyDetailsVisible = !this.isMethodologyDetailsVisible;
  }

  onBackToOverallClick() {
    const session = this.session()!;
    const criteria = {
      criterion: 'Overall',
      rating: session.subData.methodologyEvaluation?.overallScore,
      reason: session.subData.methodologyEvaluation?.overallReason,
      improvements: session.subData.methodologyEvaluation?.overallImprovements,
    };
    this.selectedMethodologyCriteria.set(criteria);
  }

  onRegenerateMethodologyClick() {
    this.evaluateMethodology(true);
  }

  onRegenerateGoalAndSessionSummaryClick() {
    this.evaluateSessionSummary();
    this.evaluateGoal();
  }

  objectionHandlingEvaluation = signal<string | undefined>(undefined);
  isLoadingObjectionHandlingEvaluation = signal<boolean>(false);

  onGenerateObjectionHandlingClick() {
    this.isLoadingObjectionHandlingEvaluation.set(true);

    const session = this.session()!;
    const messageList = this.sessionMessageList()!;

    this.openAiService
      .getAnswer$({
        messageList: [
          {
            role: 'user',
            content: evaluateObjectionHandlingPrompt(messageList, session.subData!.methodology!.description!),
          },
        ],
        config: { openaiModel: OpenaiModel.o1Mini },
      })
      .pipe(take(1))
      .subscribe(async (result) => {
        console.log('----- result:', result);

        this.objectionHandlingEvaluation.set(result);
        session.subData.objectionHandlingEvaluation = result;
        this.sessionService.update$(session).pipe(take(1)).subscribe();

        this.isLoadingObjectionHandlingEvaluation.set(false);
      });
  }

  evaluateSessionSummary() {
    const session = this.session()!;
    const messageList = this.sessionMessageList()!;

    this.isLoadingSummary.set(true);

    this.openAiService
      .getAnswer$({
        messageList: [{ role: 'user', content: getSessionSummaryPrompt(messageList) }],
        config: { openaiModel: OpenaiModel.o1Mini },
      })
      .pipe(take(1))
      .subscribe((result) => {
        session.subData.summary = result;
        this.summary.set(session.subData.summary);
        this.sessionService.update$(session).pipe(take(1)).subscribe();

        this.isLoadingSummary.set(false);
      });
  }

  evaluateGoal() {
    const session = this.session()!;
    const messageList = this.sessionMessageList()!;

    this.isLoadingGoal.set(true);

    this.openAiService
      .getAnswer$({
        messageList: [
          {
            role: 'user',
            content: getEvaluateGoalPrompt(messageList, {
              description: session.subData.goal?.description ?? '',
            }),
          },
        ],
        config: { openaiModel: OpenaiModel.o1Mini },
      })
      .pipe(take(1))
      .subscribe((result) => {
        session.subData.goalEvaluation = result;
        this.goalEvaluation.set(session.subData.goalEvaluation);
        this.sessionService.update$(session).pipe(take(1)).subscribe();

        this.isLoadingGoal.set(false);
      });
  }

  evaluateMethodology(isRescore: boolean) {
    const session = this.session()!;
    const messageList = this.sessionMessageList()!;

    this.isLoadingMethodology.set(true);

    let methodologyObservable = of(session.subData!.methodology!);

    if (isRescore) {
      methodologyObservable = this.methodologyService.getMethodologyById$(session.subData!.methodology!.id!);
    }

    methodologyObservable.pipe(take(1)).subscribe((methodology) => {
      this.openAiService
        .getAnswer$({
          messageList: [
            {
              role: 'user',
              content: getEvaluateMethodologyPrompt(messageList, methodology),
            },
          ],
          config: {
            openaiModel: OpenaiModel.gpt4,
            functions: getEvaluateMethodologyFunctions(methodology),
          },
        })
        .pipe(take(1))
        .subscribe((result) => {
          session.subData.methodologyEvaluation = result;
          this.methodologyEvaluation.set(session.subData.methodologyEvaluation);

          const overallScore = NumberUtils.average(
            session.subData.methodologyEvaluation?.evaluations.map((c) => c.rating) ?? []
          );

          session.subData.methodologyEvaluation!.overallScore = overallScore;

          this.selectedMethodologyCriteria.set({
            criterion: 'Overall',
            rating: session.subData.methodologyEvaluation?.overallScore,
            reason: session.subData.methodologyEvaluation?.overallReason,
            improvements: session.subData.methodologyEvaluation?.overallImprovements,
          });

          session.subData.score = overallScore;
          this.score.set(overallScore);

          this.sessionService.update$(session).pipe(take(1)).subscribe();

          this.isLoadingMethodology.set(false);

          // this.openAiService
          //   .getAnswer$({
          //     messageList: [
          //       {
          //         role: 'user',
          //         content: getOverallScorePrompt(result),
          //       },
          //     ],
          //     config: { openaiModel: OpenaiModel.gpt4, functions: getOverallScoreFunctions() },
          //   })
          //   .pipe(take(1))
          //   .subscribe((result) => {
          //     session.subData.score = result.score;
          //     this.score.set(session.subData.score);

          //     this.sessionService.update$(session).pipe(take(1)).subscribe();
          //   });
        });
    });
  }

  selectedMethodologyCriteria = signal<
    | {
        criterion?: string;
        rating?: number;
        reason?: string;
        improvements?: string;
      }
    | undefined
  >(undefined);
  onMethodologyContentSelect(event: any) {
    const evaluation = this.methodologyEvaluation()?.evaluations.find((c) => c.criterion === event.name);

    this.selectedMethodologyCriteria.set(evaluation);
  }

  sales = computed(() => {
    const session = this.session();
    const boardList = this.boardList();

    const creator = boardList?.find((c) => c.id === session?.createdById);

    if (!creator) {
      return {
        portrait: this.userClerkService.portraitUrl(),
        name: this.userClerkService.getUser()?.firstName,
      };
    }

    return {
      portrait: creator?.userBoardConfig?.portraitUrl,
      name: creator?.userBoardConfig?.preferredName,
    };
  });

  resume = computed(() => {
    const resume = this.session()?.subData?.resume;
    return resume;
  });

  customer = computed(() => {
    const resume = this.resume();

    return {
      portrait: resume?.subData?.content?.portraitImageUrl ?? '/assets/images/ai-profile/sofia.png',
      name: resume?.subData?.content?.firstName ?? 'Sofia',
    };
  });

  getConversationMins = computed(() => {
    const sessionMessageList = this.sessionMessageList();
    const mins = SessionMessageUtils.getConversationMins(sessionMessageList);
    return mins;
  });

  messageList = computed(() => {
    const sessionMessageList = this.sessionMessageList();

    const result: string[] = [];

    for (const sessionMessage of sessionMessageList) {
      result.push(sessionMessage.subData.customerMessage);
      result.push(sessionMessage.subData.salesMessage);
    }

    return result;
  });

  longestMonologue = computed(() => {
    const sessionMessageList = this.sessionMessageList();
    const result = SessionMessageUtils.getLongestMonologueSeconds(sessionMessageList);
    return SessionMessageUtils.convertSecondsToMinutesSeconds(result);
  });

  talkListenRatio = computed(() => {
    const sessionMessageList = this.sessionMessageList();
    const ratio = SessionMessageUtils.getTalkListRatio(sessionMessageList);
    return `${ratio.percentageA}:${ratio.percentageB}`;
  });

  fillerWordsPerMin = computed(() => {
    const sessionMessageList = this.sessionMessageList();
    return SessionMessageUtils.getFillerWordsPerMin(sessionMessageList);
  });

  talkSpeedPerMin = computed(() => {
    const sessionMessageList = this.sessionMessageList();
    return SessionMessageUtils.getTalkSpeedPerMin(sessionMessageList);
  });

  cssClassesForScore = computed(() => {
    const score = this.score() ?? 0;
    let cssClass = 'status-dot-bad';

    if (score > 3) {
      cssClass = 'status-dot-good';
    } else if (score === 3) {
      cssClass = 'status-dot-warning';
    }

    return cssClass;
  });

  cssClassesForTalkListenRatio = computed(() => {
    const sessionMessageList = this.sessionMessageList();
    const ratio = SessionMessageUtils.getTalkListRatio(sessionMessageList);

    let cssClass = 'status-dot-bad';

    if (30 <= ratio.percentageA && ratio.percentageA <= 60) {
      cssClass = 'status-dot-good';
    } else if (
      (61 <= ratio.percentageA && ratio.percentageA <= 70) ||
      (15 <= ratio.percentageA && ratio.percentageA <= 29)
    ) {
      cssClass = 'status-dot-warning';
    }

    return cssClass;
  });

  getTalkListenRatioTooltip = computed(() => {
    const cssClass = this.cssClassesForTalkListenRatio();
    if (cssClass === 'status-dot-good') {
      return 'Recommended: You provide detailed responses but also ask insightful questions and listen attentively.';
    } else if (cssClass === 'status-dot-warning') {
      return 'Take Note: You tend to speak more than you listen, but you still give the other person a chance to contribute.';
    } else {
      return 'Caution: You dominate the conversation and rarely allow the other person to share their perspective.';
    }
  });

  cssClassesForLongestMonologue = computed(() => {
    const sessionMessageList = this.sessionMessageList();
    const longestMonologue = SessionMessageUtils.getLongestMonologueSeconds(sessionMessageList);

    let cssClass = 'status-dot-bad';

    if (longestMonologue < 2 * 60) {
      cssClass = 'status-dot-good';
    } else if (2 * 60 <= longestMonologue && longestMonologue < 4 * 60) {
      cssClass = 'status-dot-warning';
    }

    return cssClass;
  });

  getLongestMonologueTooltip = computed(() => {
    const cssClass = this.cssClassesForLongestMonologue();
    if (cssClass === 'status-dot-good') {
      return 'Recommended: Your longest monologue is concise, keeping the listener engaged and promoting dialogue.';
    } else if (cssClass === 'status-dot-warning') {
      return 'Take Note: You sometimes give long explanations, which can be informative but might reduce interaction.';
    } else {
      return 'Caution: Your monologues are lengthy, which can be overwhelming and lead to disengagement.';
    }
  });

  cssClassesForFillerWords = computed(() => {
    const fillerWords = this.fillerWordsPerMin();

    let cssClass = 'status-dot-bad';

    if (fillerWords < 4) {
      cssClass = 'status-dot-good';
    } else if (4 <= fillerWords && fillerWords < 6) {
      cssClass = 'status-dot-warning';
    }

    return cssClass;
  });

  getFillerWordsTooltip = computed(() => {
    const cssClass = this.cssClassesForFillerWords();
    if (cssClass === 'status-dot-good') {
      return 'Recommended: You speak fluently without relying on filler words.';
    } else if (cssClass === 'status-dot-warning') {
      return 'Take Note: You use a few filler words here and there, but they don’t detract significantly from your message.';
    } else {
      return 'Caution: Your speech is filled with filler words, which can make you seem unprepared or unsure.';
    }
  });

  cssClassesForTalkSpeed = computed(() => {
    const talkSpeed = this.talkSpeedPerMin();

    let cssClass = 'status-dot-bad';

    if (120 <= talkSpeed && talkSpeed <= 160) {
      cssClass = 'status-dot-good';
    } else if ((100 <= talkSpeed && talkSpeed < 119) || (161 <= talkSpeed && talkSpeed <= 200)) {
      cssClass = 'status-dot-warning';
    }

    return cssClass;
  });

  getTalkSpeedTooltip = computed(() => {
    const cssClass = this.cssClassesForTalkSpeed();
    if (cssClass === 'status-dot-good') {
      return 'Recommended: Your speech pace is well-balanced, making it easy for listeners to follow along.';
    } else if (cssClass === 'status-dot-warning') {
      return 'Take Note: Your speech is a bit fast or slow, which can sometimes make it harder to keep up.';
    } else {
      return 'Caution: Your pace is either too rapid or too slow, which may make it challenging for listeners to grasp your points.';
    }
  });
}
